import _EmbedFlowImport from '@formsort/react-embed';
import { FormName, trackEvent } from '@pm/analytics';
import { useGetUtmParams, UtmParams } from '@pm/util';
import { ComponentProps, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { type FlowEventPayload, FormsortStepIds } from './formsort';
import { formsortEnvFromEnv } from './formsortHelpers';

const EmbedFlow: typeof _EmbedFlowImport =
  (_EmbedFlowImport as unknown as { default: typeof _EmbedFlowImport })
    .default ?? _EmbedFlowImport;

/**
 * Use our Formsort custom domain so the iframe is served from *.gofreddie.com.
 * This allows the embedded flow to share first-party cookies with the parent site,
 * letting Segment’s `ajs_anonymous_id` be consistent across both contexts.
 * Without this, the form would load from formsort.app (cross-site), blocking
 * cookie sharing and breaking cross-domain identity stitching in analytics.
 */
const EMBED_CONFIG = {
  origin: 'https://forms.gofreddie.com',
  autoHeight: false,
  style: { width: '100%', height: '100%' },
} as const;

// Pure utilities with no component state — safe at module level
const formatUtm = (field: string | null | undefined): string => {
  // this is really annoying, but I've actually found that the null comes through in query parameter as the string 'null', not null
  return field === null || field === 'null' || field === undefined ? '' : field;
};

const extractUTMParams = (utmParams: UtmParams) => ({
  utm_source: formatUtm(utmParams.utm_source),
  utm_medium: formatUtm(utmParams.utm_medium),
  utm_campaign: formatUtm(utmParams.utm_campaign),
  utm_term: formatUtm(utmParams.utm_term),
  utm_content: formatUtm(utmParams.utm_content),
});

const extractFieldsFromPayload = (payload: FlowEventPayload) => {
  const answers = payload?.answers ?? {};
  return {
    stepId: payload?.stepId?.toString() ?? '',
    stepIndex: payload?.stepIndex ?? 0,
    variantRevisionUuid: payload?.variantRevisionUuid?.toString() ?? '',
    responderUuid: answers?.responder_uuid?.toString() ?? '',
    variantLabel: answers?.variant_label?.toString() ?? '',
    country: answers?.['com.purposemed.bypass.country_code']?.toString() || '',
  };
};

type FormsortFormProps = Pick<
  ComponentProps<typeof EmbedFlow>,
  | 'flowLabel'
  | 'variantLabel'
  | 'queryParams'
  | 'onFlowLoaded'
  | 'onFlowFinalized'
  | 'onStepLoaded'
  | 'onStepCompleted'
  | 'onRedirect'
> & { formName: FormName; trackingEnabled?: boolean };

const FormsortForm = ({
  formName,
  trackingEnabled = true,
  flowLabel: defaultFlowLabel,
  variantLabel: defaultVariantLabel,
  queryParams: customQueryParams = [],
  onFlowLoaded,
  onFlowFinalized,
  onStepLoaded,
  onStepCompleted,
  onRedirect,
  ...props
}: FormsortFormProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flowLabel = searchParams.get('flow');
  const variantLabel = searchParams.get('variant');
  const { utm_source, utm_medium, utm_campaign, utm_term, utm_content } =
    useGetUtmParams();

  const utmTrackParams = useMemo(
    () =>
      extractUTMParams({
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
      }),
    [utm_source, utm_medium, utm_campaign, utm_term, utm_content],
  );

  const formsortEnv = formsortEnvFromEnv();

  const queryParams = useMemo(
    () =>
      Array.from(
        new Map([
          ['utm_source', utm_source ?? ''],
          ['utm_medium', utm_medium ?? ''],
          ['utm_campaign', utm_campaign ?? ''],
          ['utm_term', utm_term ?? ''],
          ['utm_content', utm_content ?? ''],
          ...customQueryParams,
        ]).entries(),
      ) as [string, string][],
    [
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      customQueryParams,
    ],
  );

  const handleFlowLoaded = useCallback(
    (payload: FlowEventPayload) => {
      if (trackingEnabled) {
        const { answers } = payload;
        trackEvent(`${formName} Flow Loaded`, {
          answerLength: answers ? Object.entries(answers).length : 0,
          flowLabel: flowLabel ?? defaultFlowLabel,
          ...extractFieldsFromPayload(payload),
          ...utmTrackParams,
        });
      }
      onFlowLoaded?.(payload);
    },
    [
      trackingEnabled,
      formName,
      flowLabel,
      defaultFlowLabel,
      utmTrackParams,
      onFlowLoaded,
    ],
  );

  const handleFlowClosed = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleFlowFinalized = useCallback(
    (payload: FlowEventPayload) => {
      if (trackingEnabled) {
        const { answers } = payload;

        trackEvent(`${formName} Flow Finalized`, {
          answerLength: answers ? Object.entries(answers).length : 0,
          flowLabel: flowLabel ?? defaultFlowLabel,
          ...extractFieldsFromPayload(payload),
          ...utmTrackParams,
        });

        const stepId = payload?.stepId;
        const isEligible = stepId === FormsortStepIds.QuestionnaireSubmitted;
        if (isEligible) {
          trackEvent(`${formName} Eligible`);
        }
      }

      onFlowFinalized?.(payload);
    },
    [
      trackingEnabled,
      formName,
      flowLabel,
      defaultFlowLabel,
      utmTrackParams,
      onFlowFinalized,
    ],
  );
  const handleRedirect = useCallback(
    (
      redirectData: Parameters<NonNullable<FormsortFormProps['onRedirect']>>[0],
    ) => {
      if (trackingEnabled && redirectData?.url) {
        const parsedUrl = new URL(redirectData.url);
        // Replace all query param values with 'REDACTED'. This is the only place I can see that could send PII to segment
        parsedUrl?.searchParams.forEach((_, key) => {
          parsedUrl?.searchParams.set(key, 'REDACTED');
        });
        const redactedUrl = parsedUrl?.toString(); // full redacted URL

        trackEvent(`${formName} Redirected`, {
          currentPath: window.location.pathname,
          targetPath: redactedUrl,
        });
      }
      onRedirect?.(redirectData);
    },
    [trackingEnabled, formName, onRedirect],
  );

  return (
    <div
      className="min-h-0 flex-1 overflow-hidden pb-l"
      // TODO: When https://github.com/formsort/oss/issues/134 is done, remove data-testid
      data-testid="formsort-form"
    >
      <EmbedFlow
        {...props}
        clientLabel="qqrXtaPDlr"
        flowLabel={flowLabel ? flowLabel : defaultFlowLabel}
        variantLabel={variantLabel ? variantLabel : defaultVariantLabel}
        embedConfig={EMBED_CONFIG}
        formsortEnv={formsortEnv}
        queryParams={queryParams}
        onFlowLoaded={handleFlowLoaded}
        onFlowClosed={handleFlowClosed}
        onFlowFinalized={handleFlowFinalized}
        onRedirect={handleRedirect}
      />
    </div>
  );
};

export default FormsortForm;
